import React, { createContext, useContext, useState, useEffect } from 'react';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState({
    tier: 'free',
    isPremium: false,
    isTrial: false,
    trialRestrictions: null,
    trialDaysRemaining: null,
    firstName: null,
    expiresAt: null,
    daysRemaining: null,
    loading: true
  });

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSubscription({
          tier: 'free',
          isPremium: false,
          isTrial: false,
          trialRestrictions: null,
          trialDaysRemaining: null,
          firstName: null,
          expiresAt: null,
          daysRemaining: null,
          loading: false
        });
        return;
      }

      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/me/subscription`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setSubscription({
          tier: data.subscription_tier,
          isPremium: data.is_premium,
          isTrial: data.is_trial || false,
          trialRestrictions: data.trial_restrictions || null,
          trialDaysRemaining: data.trial_days_remaining || null,
          firstName: data.first_name || null,
          expiresAt: data.subscription_expires_at,
          daysRemaining: data.days_remaining,
          loading: false
        });
      } else {
        // Token invalid, clear subscription
        setSubscription({
          tier: 'free',
          isPremium: false,
          isTrial: false,
          trialRestrictions: null,
          trialDaysRemaining: null,
          firstName: null,
          expiresAt: null,
          daysRemaining: null,
          loading: false
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('Subscription fetch timeout');
      } else {
        console.error('Error fetching subscription:', error);
      }
      // Set default to prevent infinite loading
      setSubscription({
        tier: 'free',
        isPremium: false,
        isTrial: false,
        trialRestrictions: null,
        trialDaysRemaining: null,
        firstName: null,
        expiresAt: null,
        daysRemaining: null,
        loading: false
      });
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const refreshSubscription = async () => {
    await fetchSubscription();
  };

  const clearSubscription = () => {
    setSubscription({
      tier: 'free',
      isPremium: false,
      isTrial: false,
      trialRestrictions: null,
      trialDaysRemaining: null,
      firstName: null,
      expiresAt: null,
      daysRemaining: null,
      loading: false
    });
  };

  return (
    <SubscriptionContext.Provider value={{ ...subscription, refreshSubscription, clearSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
};